import React, { useEffect, useState } from 'react';
import { Table, Input, message, Button, Modal, Form, Select, Space, InputNumber } from 'antd';
import { SearchOutlined, EditOutlined } from '@ant-design/icons';
import { WordPressAPI } from '../../services/api/api';
import { WPOption, CustomOption, OptionValueType, DateFormatOption, NumberFormatOption, CurrencyFormatOption, MeasurementFormatOption, PercentageFormatOption, WCPallet } from '../../types/wordpress';
import type { ColumnsType } from 'antd/es/table';
import './styles.css';
import { decodeHtmlEntities } from 'utils/helpers';

const OptionsManager: React.FC = () => {
    const [options, setOptions] = useState<WPOption[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingOption, setEditingOption] = useState<CustomOption | null>(null);
    const [form] = Form.useForm();
    const [pallets, setPallets] = useState<WCPallet[]>([]);

    const fetchOptions = async () => {
        setLoading(true);
        try {
            const data = await WordPressAPI.getOptions();
            setOptions(Array.isArray(data) ? data : []);
        } catch (error) {
            message.error('Failed to fetch options');
            console.error('Error fetching options:', error);
            setOptions([]);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (record: WPOption) => {
        try {
            const customOption = JSON.parse(record.option_value) as CustomOption;
            setEditingOption(customOption);
            form.setFieldsValue({ option_value: customOption.option_value });
            setIsModalVisible(true);
        } catch (error) {
            message.error('Invalid option format');
        }
    };

    const handleModalClose = () => {
        setIsModalVisible(false);
        setEditingOption(null);
        form.resetFields();
    };

    const handleUpdate = async (values: { option_value: OptionValueType }) => {
        if (!editingOption) return;

        try {
            // Start of Selection
            const updatedOption = {
                ...editingOption,
                option_value: values.option_value
            };
            await WordPressAPI.updateOption(
                editingOption.option_name,
                JSON.stringify(updatedOption)
            );
            message.success('Option updated successfully');
            handleModalClose();
            fetchOptions();
        } catch (error) {
            message.error('Failed to update option');
            console.error('Error updating option:', error);
        }
    };

    const formatOptionValue = (customOption: CustomOption) => {
        switch (customOption.option_type) {
            case 'date':
                const dateValue = customOption.option_value as DateFormatOption;
                return (
                    <>
                        <div>Date Format: {dateValue.format}</div>
                        <div>Time Format: {dateValue.time_format}</div>
                    </>
                );

            case 'number':
                const numberValue = customOption.option_value as NumberFormatOption;
                return (
                    <>
                        <div>Decimal Separator: {numberValue.decimal_separator}</div>
                        <div>Thousand Separator: {numberValue.thousand_separator}</div>
                        <div>Decimals: {numberValue.decimals}</div>
                    </>
                );

            case 'currency':
                const currencyValue = customOption.option_value as CurrencyFormatOption;
                return (
                    <>
                        <div>Symbol: {currencyValue.symbol}</div>
                        <div>Position: {currencyValue.position}</div>
                        <div>Decimal Separator: {currencyValue.decimal_separator}</div>
                        <div>Thousand Separator: {currencyValue.thousand_separator}</div>
                        <div>Decimals: {currencyValue.decimals}</div>
                    </>
                );

            case 'measurement':
                const measurementValue = customOption.option_value as MeasurementFormatOption;
                return (
                    <>
                        <div>Weight Unit: {measurementValue.weight_unit}</div>
                        <div>Dimension Unit: {measurementValue.dimension_unit}</div>
                        <div>Decimal Separator: {measurementValue.decimal_separator}</div>
                        <div>Thousand Separator: {measurementValue.thousand_separator}</div>
                        <div>Decimals: {measurementValue.decimals}</div>
                    </>
                );

            case 'percentage':
                const percentValue = customOption.option_value as PercentageFormatOption;
                return (
                    <div>Value: {percentValue.value}%</div>
                );

            // Start of Selection
            case 'pallet':
                const palletValue = customOption.option_value as WCPallet;
                return (
                    <div>
                        Pallet Type: {pallets ? decodeHtmlEntities(pallets.find(pallet => pallet.id === palletValue.id)?.title.rendered || '') : ''}

                    </div>
                );

            default:
                return String(customOption.option_value);
        }
    };

    const columns: ColumnsType<WPOption> = [

        {
            title: 'Display Name',
            key: 'display_name',
            render: (_, record) => {
                try {
                    const customOption = JSON.parse(record.option_value) as CustomOption;
                    return customOption.display_name;
                } catch {
                    return '-';
                }
            },
        },
        {
            title: 'Type',
            key: 'type',
            render: (_, record) => {
                try {
                    const customOption = JSON.parse(record.option_value) as CustomOption;
                    return customOption.option_type;
                } catch {
                    return '-';
                }
            },
        },
        {
            title: 'Value',
            key: 'value',
            render: (_, record) => {
                try {
                    const customOption = JSON.parse(record.option_value) as CustomOption;
                    return formatOptionValue(customOption);
                } catch {
                    return record.option_value;
                }
            },
            ellipsis: true,
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Button
                    type="primary"
                    ghost
                    icon={<EditOutlined />}
                    onClick={() => handleEdit(record)}
                >
                    Edit
                </Button>
            ),
        },
    ];

    const renderOptionValueInput = () => {
        if (!editingOption) return null;

        switch (editingOption.option_type) {
            case 'date':
                return (
                    <>
                        <Form.Item name={['option_value', 'format']} label="Date Format">
                            <Select>
                                <Select.Option value="MMM d, yyyy">Sep 1, 2024</Select.Option>
                                <Select.Option value="yyyy-MM-dd">2024-09-01</Select.Option>
                                <Select.Option value="dd/MM/yyyy">01/09/2024</Select.Option>
                            </Select>
                        </Form.Item>
                        <Form.Item name={['option_value', 'time_format']} label="Time Format">
                            <Select>
                                <Select.Option value="HH:mm">24-hour (14:30)</Select.Option>
                                <Select.Option value="hh:mm a">12-hour (02:30 PM)</Select.Option>
                            </Select>
                        </Form.Item>
                    </>
                );

            case 'currency':
            case 'number':
            case 'measurement':
                return (
                    <>
                        {editingOption.option_type === 'currency' && (
                            <Form.Item name={['option_value', 'symbol']} label="Symbol">
                                <Input />
                            </Form.Item>

                        )}
                        {editingOption.option_type === 'currency' && (
                            <Form.Item name={['option_value', 'position']} label="Position">
                                <Select>
                                    <Select.Option value="before">Before</Select.Option>
                                    <Select.Option value="after">After</Select.Option>
                                </Select>
                            </Form.Item>

                        )}
                        {editingOption.option_type === 'measurement' && (
                            <>
                                <Form.Item name={['option_value', 'weight_unit']} label="Weight Unit">
                                    <Select>
                                        <Select.Option value="kg">Kilograms (kg)</Select.Option>
                                        <Select.Option value="lbs">Pounds (lbs)</Select.Option>
                                    </Select>
                                </Form.Item>
                                <Form.Item name={['option_value', 'dimension_unit']} label="Dimension Unit">
                                    <Select>
                                        <Select.Option value="cm">Centimeters (cm)</Select.Option>
                                        <Select.Option value="inch">Inches (inch)</Select.Option>
                                    </Select>
                                </Form.Item>
                            </>
                        )}
                        <Form.Item name={['option_value', 'decimal_separator']} label="Decimal Separator">
                            <Select>
                                <Select.Option value=".">. (Period)</Select.Option>
                                <Select.Option value=",">, (Comma)</Select.Option>
                            </Select>
                        </Form.Item>
                        <Form.Item name={['option_value', 'thousand_separator']} label="Thousand Separator">
                            <Select>
                                <Select.Option value=",">, (Comma)</Select.Option>
                                <Select.Option value=".">. (Period)</Select.Option>
                                <Select.Option value=" ">Space</Select.Option>
                            </Select>
                        </Form.Item>
                        <Form.Item name={['option_value', 'decimals']} label="Decimal Places">
                            <InputNumber min={0} max={4} />
                        </Form.Item>
                    </>
                );

            case 'percentage':
                const percentageValue = editingOption.option_value as PercentageFormatOption;
                return (
                    <Form.Item
                        name={['option_value', 'value']}
                        label="Percentage"
                        rules={[{ required: true }]}
                    >
                        <InputNumber
                            min={percentageValue.min}
                            max={percentageValue.max}
                            formatter={value => `${value}%`}
                            parser={value => parseFloat(value?.replace('%', '') || '0')}
                        />
                    </Form.Item>
                );

            case 'pallet':
                const palletId = editingOption.option_value as number;
                return (
                    <Form.Item
                        name={['option_value', 'id']}
                        label="Pallet"
                        rules={[{ required: true }]}
                    >
                        <Select>
                            {pallets.map(pallet => (
                                <Select.Option key={pallet.id} value={pallet.id}>
                                    {pallet.title.rendered}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                );
            default:
                return (
                    <Form.Item name="option_value" label="Value">
                        <Input.TextArea rows={4} />
                    </Form.Item>
                );
        }
    };

    const getFilteredOptions = () => {
        return options.filter(option => {
            try {
                const searchLower = searchText.toLowerCase();
                const customOption = JSON.parse(option.option_value) as CustomOption;

                return (
                    option.option_name.toLowerCase().includes(searchLower) ||
                    customOption.display_name.toLowerCase().includes(searchLower) ||
                    customOption.option_type.toLowerCase().includes(searchLower) ||
                    JSON.stringify(customOption.option_value).toLowerCase().includes(searchLower)
                );
            } catch {
                return option.option_name.toLowerCase().includes(searchText.toLowerCase());
            }
        });
    };

    useEffect(() => {
        fetchPallets();
        fetchOptions();
    }, []);

    const fetchPallets = async () => {
        try {
            const response = await WordPressAPI.getPallets({});
            setPallets(response.items);
        } catch (error) {
            message.error('Failed to fetch pallets');
            console.error('Error fetching pallets:', error);
        }
    };

    return (
        <div className="options-manager">
            <div className="options-manager-header">
                <h2>WordPress Options</h2>
                <div className="header-actions">
                    <Input
                        placeholder="Search options..."
                        prefix={<SearchOutlined />}
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                        style={{ width: 300 }}
                        allowClear
                    />
                </div>
            </div>

            <Table
                columns={columns}
                dataSource={getFilteredOptions()}
                loading={loading}
                rowKey="option_name"

                pagination={{
                    showSizeChanger: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                }}
            />

            <Modal
                title="Edit Option"
                open={isModalVisible}
                onCancel={handleModalClose}
                footer={null}
                destroyOnClose={true}
            >
                <Form
                    form={form}
                    onFinish={handleUpdate}
                    layout="vertical"
                >

                    <Form.Item label="Display Name">
                        <Input value={editingOption?.display_name} disabled />
                    </Form.Item>
                    <Form.Item label="Type">
                        <Input value={editingOption?.option_type} disabled />
                    </Form.Item>
                    {renderOptionValueInput()}
                    <Form.Item>
                        <div style={{ textAlign: 'right' }}>
                            <Button onClick={handleModalClose} style={{ marginRight: 8 }}>
                                Cancel
                            </Button>
                            <Button type="primary" htmlType="submit">
                                Update Option
                            </Button>
                        </div>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default OptionsManager; 